import React, { createContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase'; 
import { toast } from 'react-toastify';

const ProjectContext = createContext();

const ProjectProvider = ({ children }) => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  const fetchProjectsFallback = async () => {
    try {
      const response = await fetch('/data/projects.json');
      if (!response.ok) throw new Error('Failed to fetch fallback data');
      const data = await response.json();
      return data || [];
    } catch (err) {
      console.error('Failed to load JSON fallback:', err);
      return [];
    }
  };

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // If Supabase returns data, use it
      if (data && data.length > 0) {
        console.log('Fetched Projects from Supabase:', data, 'Count:', data.length);
        setProjects(data);
        setError(null);
      } else {
        // If Supabase returns empty, use fallback
        console.warn('Supabase returned empty, using JSON fallback');
        const fallbackData = await fetchProjectsFallback();
        setProjects(fallbackData);
        setError(null);
      }
    } catch (err) {
      // If Supabase fails, use JSON fallback
      console.error('Fetch Projects Error:', err.message);
      console.warn('Using JSON fallback due to Supabase error');
      const fallbackData = await fetchProjectsFallback();
      setProjects(fallbackData);
      setError(null); // Don't show error to user, just use fallback
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchUserAndProjects = async () => {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) {
        console.error('Error fetching user:', error);
        // Don't set error for user fetch failure; proceed to fetch projects
      } else {
        console.log('Fetched User:', user);
        setUser(user);
      }
      // Fetch projects regardless of user authentication
      await fetchProjects();
      setLoading(false);
    };
    fetchUserAndProjects();
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      const newUser = session?.user ?? null;
      console.log('Auth State Changed:', event, newUser);
      setUser(newUser);
      // No need to refetch projects on auth change; already fetched
    });
    return () => authListener.subscription.unsubscribe();
  }, [fetchProjects]);

  const createProject = async (projectData) => {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('You must be logged in to create a project');
    }
    const { data, error } = await supabase.from('projects').insert([projectData]).select();
    if (error) {
      console.error('Create Project Error:', error);
      throw error;
    }
    setProjects((prev) => [...prev, ...data]);
  };

  const updateProject = async (id, projectData) => {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('You must be logged in to update a project');
    }
    try {
      const { data: currentProject, error: fetchError } = await supabase
        .from('projects')
        .select('logo, logo_background, video, description')
        .eq('id', id)
        .single();
      if (fetchError) throw fetchError;

      console.log('Current Project Files:', currentProject);

      const oldFilePaths = [];
      const bucketName = 'project-files';

      const extractPath = (url) => {
        if (!url || typeof url !== 'string') return null;
        const path = url.split(`/${bucketName}/`)[1];
        return path ? path : null;
      };

      if (currentProject.logo) {
        const path = extractPath(currentProject.logo);
        if (path && projectData.logo !== currentProject.logo) oldFilePaths.push(path);
      }
      if (currentProject.logo_background) {
        const path = extractPath(currentProject.logo_background);
        if (path && projectData.logo_background !== currentProject.logo_background) oldFilePaths.push(path);
      }
      if (currentProject.video) {
        const path = extractPath(currentProject.video);
        if (path && projectData.video !== currentProject.video) oldFilePaths.push(path);
      }
      if (currentProject.description && Array.isArray(currentProject.description)) {
        currentProject.description.forEach((block, index) => {
          if ((block.type === 'image' || block.type === 'video') && block.url) {
            const path = extractPath(block.url);
            const newBlock = projectData.description?.[index];
            if (path && (!newBlock || newBlock.url !== block.url)) oldFilePaths.push(path);
          }
        });
      }

      console.log('Old Files to Delete:', oldFilePaths);

      if (oldFilePaths.length > 0) {
        const { error: storageError } = await supabase.storage
          .from(bucketName)
          .remove(oldFilePaths);
        if (storageError) {
          console.error('Storage Deletion Error:', storageError);
          throw storageError;
        }
      }

      const { data, error } = await supabase
        .from('projects')
        .update(projectData)
        .eq('id', id)
        .select();
      if (error) {
        console.error('Update Project Error:', error);
        throw error;
      }

      setProjects((prev) =>
        prev.map((project) => (project.id === id ? { ...project, ...data[0] } : project))
      );
    } catch (error) {
      console.error('Update Project Error:', error);
      throw error;
    }
  };

  const deleteProject = async (id) => {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('You must be logged in to delete a project');
    }
    try {
      const { data: project, error: fetchError } = await supabase
        .from('projects')
        .select('logo, logo_background, video, description')
        .eq('id', id)
        .single();
      if (fetchError) throw fetchError;

      console.log('Project Data:', project);

      const filePaths = [];
      const bucketName = 'project-files';

      const extractPath = (url) => {
        if (!url || typeof url !== 'string') return null;
        const path = url.split(`/${bucketName}/`)[1];
        return path ? path : null;
      };

      if (project.logo) {
        const path = extractPath(project.logo);
        if (path) filePaths.push(path);
      }
      if (project.logo_background) {
        const path = extractPath(project.logo_background);
        if (path) filePaths.push(path);
      }
      if (project.video) {
        const path = extractPath(project.video);
        if (path) filePaths.push(path);
      }
      if (project.description && Array.isArray(project.description)) {
        project.description.forEach((block) => {
          if ((block.type === 'image' || block.type === 'video') && block.url) {
            const path = extractPath(block.url);
            if (path) filePaths.push(path);
          }
        });
      }

      console.log('Files to Delete:', filePaths);

      if (filePaths.length > 0) {
        const { error: storageError } = await supabase.storage
          .from(bucketName)
          .remove(filePaths);
        if (storageError) {
          console.error('Storage Deletion Error:', storageError);
          throw storageError;
        }
      }

      const { error: deleteError } = await supabase
        .from('projects')
        .delete()
        .eq('id', id);
      if (deleteError) {
        throw deleteError;
      }

      setProjects((prev) => prev.filter((project) => project.id !== id));
    } catch (error) {
      console.error('Delete Project Error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUser(null);
      setProjects([]);
      setSelectedProject(null);
      setError(null);
      toast.success('Logged out successfully!', {
        position: 'bottom-right',
        autoClose: 3000,
        className: 'bg-blue-600 text-white',
      });
    } catch (error) {
      console.error('Logout Error:', error);
      toast.error(`Logout failed: ${error.message}`, {
        position: 'bottom-right',
        autoClose: 3000,
        className: 'bg-red-600 text-white',
      });
    }
  };

  return (
    <ProjectContext.Provider
      value={{
        projects,
        selectedProject,
        setSelectedProject,
        loading,
        error,
        createProject,
        updateProject,
        deleteProject,
        fetchProjects,
        user,
        logout,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export { ProjectContext, ProjectProvider };